'use client';

import type { ProductCardData } from '@/components/brand';
import { Button, Icon } from '@/components/ui';
import { useAddToCart } from '@/hooks/useAddToCart';
import { useIsCheckoutBusy } from '@/hooks/useCheckout';
import { useWishlist } from '@/hooks/useWishlist';
import type { Product } from '@/lib/product/types';
import { devError } from '@/lib/utils/devLog';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import styles from './PdpHero.module.scss';

export interface PDPHeroProps {
  product: Product;
  className?: string;
}

/**
 * Format a URL slug to display name (e.g., "flying-officer" -> "Flying Officer")
 */
function formatSlugToDisplayName(slug: string): string {
  return slug
    .split('-')
    .map((word) => {
      // Keep common acronyms uppercase
      const acronyms = ['avi', 'gmt', 'utc'];
      if (acronyms.includes(word.toLowerCase())) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

/**
 * PDP Hero section with full-bleed background image and product info overlay
 * Similar to Breitling's sticky hero section
 */
export function PDPHero({ product, className }: PDPHeroProps) {
  const t = useTranslations('product.cta');
  const {
    name,
    collection,
    price,
    heroImage,
    shortDescription,
    id,
    sku,
    caseMaterial,
    dialColor,
    warrantyExtensionDuration,
  } = product;
  const { addToCart, addingProductId } = useAddToCart();
  const isCheckoutBusy = useIsCheckoutBusy();
  const { isInWishlist, toggleItem } = useWishlist();

  // Check if product is in wishlist
  const inWishlist = isInWishlist(sku);
  console.log('[PDPHero] Wishlist check:', { sku, inWishlist });

  // Handle wishlist toggle
  const handleWishlistToggle = () => {
    toggleItem({
      sku,
      price: price.amount,
      theCurrency: price.currency,
      type: 'watch',
      itemListName: 'PDP',
    });
  };

  // Format collection from slug to display name
  const collectionDisplay = formatSlugToDisplayName(collection);

  // Handler for add to bag
  const handleAddToBag = () => {
    if (!id) {
      devError('[PDPHero] Cannot add to cart: missing variant id');
      return;
    }

    // Clean attribute value - remove " | ID" suffix if present
    // e.g., "18k red gold | 18KREG" → "18k red gold"
    const cleanValue = (val: string | undefined) => {
      if (!val) return undefined;
      const pipeIndex = val.indexOf('|');
      return pipeIndex !== -1 ? val.substring(0, pipeIndex).trim() : val.trim();
    };

    // Build variant descriptor for better optimistic display
    const cleanedMaterial = cleanValue(caseMaterial);
    const cleanedColor = cleanValue(dialColor);
    const variantParts = [cleanedMaterial, cleanedColor].filter(Boolean);
    const variantName = variantParts.length > 0 ? variantParts.join(' - ') : undefined;

    // Debug logging - trace optimistic data
    console.log('[PDPHero] Add to cart - optimistic data:', {
      rawCaseMaterial: caseMaterial,
      rawDialColor: dialColor,
      cleanedMaterial,
      cleanedColor,
      variantName,
    });

    const productCard: ProductCardData = {
      id: id || sku,
      variantId: id, // id is the Saleor variant ID
      sku,
      name,
      // Pass variant info for better optimistic display
      subtitle: variantName,
      material: cleanedMaterial,
      dialColor: cleanedColor,
      price: price.amount,
      priceAmount: price.amount,
      currency: price.currency,
      image: heroImage?.url || '',
      href: '', // Not needed for cart
      // Warranty extension duration for optimistic cart display
      warrantyExtensionDuration,
    };

    addToCart(productCard);
  };

  const isAddingThisProduct = addingProductId === (id || sku);

  return (
    <section className={clsx(styles.hero, className)}>
      {/* Background Image */}
      {heroImage && (
        <div className={styles.background}>
          <Image
            src={heroImage.url}
            alt={heroImage.alt}
            fill
            className={styles.backgroundImage}
            priority
            sizes="100vw"
          />
          <div className={styles.overlay} />
        </div>
      )}

      {/* Content */}
      <div className={styles.container}>
        <div className={styles.content}>
          {/* Title */}
          <h1 className={styles.title}>
            <span className={styles.collection}>{collectionDisplay}</span>
            <span className={styles.model}>{name.replace(collectionDisplay, '').trim()}</span>
          </h1>

          {/* Subtitle */}
          {shortDescription && <p className={styles.subtitle}>{shortDescription}</p>}
        </div>

        {/* Bottom Bar */}
        <div className={styles.bottomBar}>
          <span className={styles.price}>{price.formatted}</span>
          <div className={styles.actions}>
            <button
              className={clsx(styles.wishlistButton, inWishlist && styles.wishlistButtonActive)}
              onClick={handleWishlistToggle}
              type="button"
              aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <Icon
                name={inWishlist ? 'heart-filled' : 'heart-outline'}
                size={20}
                className={styles.icon}
              />
            </button>
            <Button
              variant="secondary"
              size="lg"
              onClick={handleAddToBag}
              disabled={!id || isCheckoutBusy}
              loading={isAddingThisProduct}
              iconAfter={<Icon name="shopping-bag" size={20} aria-hidden="true" />}
              className={styles.addToBag}
            >
              {t('addToBag')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

PDPHero.displayName = 'PDPHero';
